import { invoiceIntentOptions } from "./payment-contract";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { EmptyState } from "@/components/empty-state";
import { PayPage } from "@/components/pay-page";
import { PaymentSettled } from "@/components/payment-settled";
import { CheckoutSkeleton } from "../checkout/checkout-skeleton";
import { CoachHeader } from "../checkout/coach-header";
import { InvoiceReceipt } from "./invoice-receipt";
import { InvoiceSheet } from "./invoice-sheet";
import { InvoiceSummary } from "./invoice-summary";
import { useInvoiceDetails } from "@/data/invoice";
import { useCoachColour } from "@/design/use-coach-colour";
import { coachLabel, sellerOf, sentence } from "@/utils/coach";
import {
  fontSource,
  stripeAppearance,
  stripeLocale,
} from "@/stripe/appearance";
import type { InvoiceOutcome } from "./use-invoice-payment";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";

/**
 * The page an invoice email opens (template checkout 1.4, invoice variant).
 *
 * The answer first (what is owed, by when), then what is invoiced, then who it is for, then one
 * way to pay. The details modal and the uppercase eyebrow grid it held are deleted.
 *
 * The coach header signs every state (open, paid, void, uncollectible) with the seller the
 * backend resolves; it is left out only when the coach truly has no name (see `sellerOf`).
 */

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "";

export function InvoicePage() {
  const { t, i18n } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { data, isLoading, isError, error, refetch, isRefetching } =
    useInvoiceDetails(invoiceId);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeBlocked, setStripeBlocked] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [done, setDone] = useState<InvoiceOutcome | null>(null);

  /* Orange's one job, handed to the coach, from the moment the payload lands. */
  const coachHex = useCoachColour(data?.business.brandColor);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;

    void (async () => {
      try {
        const instance = await loadStripe(STRIPE_KEY, {
          stripeAccount: data.stripeAccountId,
        });
        if (cancelled) return;
        if (!instance) {
          setStripeBlocked(true);
          setStripeLoading(false);
          return;
        }
        setStripe(instance);

        const params = new URLSearchParams(window.location.search);
        const clientSecret = params.get("payment_intent_client_secret");
        if (clientSecret) {
          const { paymentIntent } =
            await instance.retrievePaymentIntent(clientSecret);
          if (cancelled) return;
          if (
            paymentIntent?.status === "succeeded" ||
            paymentIntent?.status === "processing"
          ) {
            setDone({
              kind: paymentIntent.status,
              email: paymentIntent.receipt_email ?? data.customer.email ?? "",
            });
          }
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch {
        if (!cancelled) setStripeBlocked(true);
      } finally {
        if (!cancelled) setStripeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const coachName = coachLabel(data?.business.name, t);
  const supportEmail = data?.business.supportEmail;
  const language = i18n.resolvedLanguage ?? i18n.language;

  if (isLoading || !invoiceId || (data && stripeLoading)) {
    return (
      <PayPage>
        <main>
          <CheckoutSkeleton />
        </main>
      </PayPage>
    );
  }

  if (isError || !data) {
    const status = (error as { response?: { status?: number } } | null)
      ?.response?.status;
    const gone = status === 404 || status === 400;
    return (
      <PayPage>
        <main>
          <EmptyState
            heading={
              gone
                ? t("gymkeePay.invoice.notFoundHeading")
                : t("gymkeePay.invoice.loadFailedHeading")
            }
            line={gone ? t("gymkeePay.invoice.notFoundLine") : undefined}
            action={
              gone ? (
                supportEmail ? (
                  <Button variant="tertiary" href={`mailto:${supportEmail}`}>
                    {t("gymkeePay.common.messageCoach", { coach: coachName })}
                  </Button>
                ) : undefined
              ) : (
                <Button
                  variant="secondary"
                  loading={isRefetching}
                  onClick={() => void refetch()}
                >
                  {t("gymkeePay.common.tryAgain")}
                </Button>
              )
            }
          />
        </main>
      </PayPage>
    );
  }

  const seller = sellerOf(data.business);
  const header = seller ? (
    <CoachHeader seller={seller} />
  ) : null;

  const status = data.invoice.status;
  const settled = !!done || status === "paid";

  if (settled) {
    return (
      <PayPage>
        <main className="gap-gk-6 flex flex-col">
          {header}
          {/* The beat only when the payment happened here; an invoice that was already paid
              before this page opened has nothing to draw. */}
          {done ? (
            <PaymentSettled>
              <InvoiceReceipt
                invoice={data}
                coachName={coachName}
                status={done.kind}
              />
            </PaymentSettled>
          ) : (
            <InvoiceReceipt
              invoice={data}
              coachName={coachName}
              status="succeeded"
            />
          )}
        </main>
      </PayPage>
    );
  }

  if (status === "void" || status === "uncollectible" || status === "draft") {
    const cancelled = status === "void";
    return (
      <PayPage>
        <main className="gap-gk-6 flex flex-col">
          {header}
          <EmptyState
            heading={
              cancelled
                ? t("gymkeePay.invoice.voidHeading")
                : t("gymkeePay.invoice.uncollectibleHeading")
            }
            line={sentence(
              t("gymkeePay.invoice.voidLine", { coach: coachName }),
            )}
            action={
              supportEmail ? (
                <Button variant="tertiary" href={`mailto:${supportEmail}`}>
                  {t("gymkeePay.common.messageCoach", { coach: coachName })}
                </Button>
              ) : undefined
            }
          />
        </main>
      </PayPage>
    );
  }

  const elementsOptions: StripeElementsOptions = {
    ...invoiceIntentOptions(data),
    appearance: stripeAppearance(),
    fonts: [fontSource()],
    locale: stripeLocale(language),
  };

  return (
    <PayPage>
      <main className="gap-gk-6 flex flex-col">
        {header}
        <InvoiceSummary invoice={data} />
        {stripeBlocked || !stripe ? (
          <Alert
            tone="warning"
            action={
              <Button
                variant="tertiary"
                onClick={() => window.location.reload()}
              >
                {t("gymkeePay.common.reload")}
              </Button>
            }
          >
            {t("gymkeePay.common.stripeBlocked")}
          </Alert>
        ) : (
          <Elements
            key={coachHex ?? "gymkee"}
            stripe={stripe}
            options={elementsOptions}
          >
            <InvoiceSheet
              invoice={data}
              coachName={coachName}
              onDone={setDone}
            />
          </Elements>
        )}
      </main>
    </PayPage>
  );
}
